import { useQuery } from "react-query"
import { getBeaconLocations, getMobileLocations } from "../../services/calls/positions"
import { useTime } from "../../hooks/useTime"
import { useEffect, useMemo, useState } from "react"
import { MOBILE_TYPE } from "./data"
import { ICON } from "../../components/Icon/data"
import { COLOR, SINGLE_TIME_UNIT } from "../../data/variables"
import { Point } from "../../components/FlotiMap/context/types"

type UseLiveMap = {
  refetchInterval?: number
  fleets?: number[]
  fetchData?: boolean
}

export const useLiveMap = ({ refetchInterval = 5000, fleets = [], fetchData = true }: UseLiveMap) => {
  const { calculateTimeDiff, minutesToLargestUnit } = useTime()

  const [mobilePoints, setMobilePoints] = useState<Point[]>([])
  const [beaconPoints, setBeaconPoints] = useState<Point[]>([])
  
  const { data: mobileData, isLoading: isMobilesLoading } = useQuery({
    queryKey: 'getMobileLocations',
    queryFn: () => getMobileLocations(fleets),
    refetchInterval,
    enabled: fetchData
  })

  const { data: beaconData, isLoading: isBeaconsLoading } = useQuery({
    queryKey: 'getBeaconsLocations',
    queryFn: () => getBeaconLocations({ fleet: fleets }),
    refetchInterval,
    enabled: fetchData
  })

  useEffect(() => {
    if (mobileData) {
      const points = mobileData.mobile_locations?.map((point) => {
        const {
          lat, lon, mobile_globalid, icon_color, mobileid, heading, icon, stop_start_date, speed,
        } = point
        const marker = icon?.split('_')?.[0] ?? 'arrow'
        let iconMarker = marker === 'arrow' ? ICON.MAP_MARKER : ICON.MARKER_CIRCLE
        let color = icon_color
        const markerPoint: Point = {
          lat: Number(lat),
          lon: Number(lon),
          heading,
          name: mobile_globalid,
          icon: iconMarker ,
          color,
          id: mobileid,
          mobile_type: MOBILE_TYPE.MOBILE
        }

        if (!Number(speed) && stop_start_date) {
          markerPoint.icon = ICON.STOP_SIGN
          markerPoint.color = COLOR.RED_70
          markerPoint.scale = 90
          markerPoint.heading = '0'
          const stopTime = calculateTimeDiff(stop_start_date);
          const { unit, value } = minutesToLargestUnit(stopTime)
          markerPoint.text = `${value.toFixed(0)}${SINGLE_TIME_UNIT[unit]}`
        }

        return markerPoint
      })

      setMobilePoints(points)
    }
  }, [mobileData])

  useEffect(() => {
    if (beaconData) {
      const points = beaconData.map((point) => {
        const { poslat, poslon, mobile_globalid, mobile } = point
        const beaconPoint: Point = {
          lat: Number(poslat),
          lon: Number(poslon),
          heading: '0',
          name: mobile_globalid,
          icon: ICON.BEACON_MARKER,
          id: mobile,
          mobile_type: MOBILE_TYPE.BEACON
        }
        return beaconPoint
      })

      setBeaconPoints(points)
    }
  }, [beaconData])

  const isLoading = useMemo(() => isMobilesLoading || isBeaconsLoading, [isMobilesLoading, isBeaconsLoading])

  const rawData = useMemo(() => [...mobileData?.mobile_locations ?? [], ...beaconData ?? []], [mobileData, beaconData])
  const points = useMemo(() => !isLoading ? [...mobilePoints, ...beaconPoints] : [], [isLoading, mobilePoints, beaconPoints])

  return {
    isLoading,
    rawData,
    points
  }
}